import { supabase } from '../lib/supabase';

export const trackingService = {

  async assignBestTechnician(appointmentData) {
    const { scheduled_date, scheduled_time_start, scheduled_time_end, service_type, urgency, customer_latitude, customer_longitude } = appointmentData;

    try {
      // 1. RÉCUPÉRER TOUS LES TECHNICIENS ACTIFS
      const { data: allTechnicians, error: techError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('role', 'technicien')
        .eq('is_active', true);

      if (techError || !allTechnicians || allTechnicians.length === 0) {
        console.error('Erreur récupération techniciens:', techError);
        return null;
      }

      // 2. RÉCUPÉRER LES RENDEZ-VOUS EXISTANTS POUR CETTE DATE
      const { data: existingAppointments, error: apptError } = await supabase
        .from('appointments')
        .select('technician, scheduled_time_start, scheduled_time_end, customer_latitude, customer_longitude')
        .eq('scheduled_date', scheduled_date)
        .in('status', ['planned', 'confirmed', 'en_route']);

      if (apptError) {
        console.error('Erreur récupération appointments:', apptError);
      }

      // 3. RÉCUPÉRER LES ABSENCES POUR CETTE DATE
      const { data: timeoffData, error: timeoffError } = await supabase
        .from('technician_timeoff')
        .select('technician_id, start_date, end_date, start_time, end_time, is_all_day')
        .lte('start_date', scheduled_date)
        .gte('end_date', scheduled_date);

      if (timeoffError) {
        console.error('Erreur récupération absences:', timeoffError);
      }

      // 4. VÉRIFIER DISPONIBILITÉ DU CRÉNEAU (incluant les absences)
      const isSlotAvailable = (technicianId, newStart, newEnd, date) => {
        // Vérifier d'abord les absences
        const technicianTimeoff = timeoffData?.filter(to => to.technician_id === technicianId) || [];
        
        for (const timeoff of technicianTimeoff) {
          // Si l'absence couvre cette date
          if (timeoff.start_date <= date && (timeoff.end_date === null || timeoff.end_date >= date)) {
            // Si c'est toute la journée, indisponible
            if (timeoff.is_all_day) {
              return false;
            }
            
            // Si c'est une absence à heures spécifiques, vérifier le chevauchement
            if (timeoff.start_time && timeoff.end_time) {
              if (
                (newStart >= timeoff.start_time && newStart < timeoff.end_time) ||
                (newEnd > timeoff.start_time && newEnd <= timeoff.end_time) ||
                (newStart <= timeoff.start_time && newEnd >= timeoff.end_time)
              ) {
                return false;
              }
            }
          }
        }

        // Ensuite vérifier les rendez-vous existants
        if (!existingAppointments) return true;

        const techAppointments = existingAppointments.filter(apt => apt.technician === technicianId);
        
        for (const apt of techAppointments) {
          const aptStart = apt.scheduled_time_start;
          const aptEnd = apt.scheduled_time_end;

          if (
            (newStart >= aptStart && newStart < aptEnd) ||
            (newEnd > aptStart && newEnd <= aptEnd) ||
            (newStart <= aptStart && newEnd >= aptEnd)
          ) {
            return false;
          }
        }

        return true;
      };

      // 5. CALCULER LA DISTANCE (Haversine)
      const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
          Math.sin(dLat/2) * Math.sin(dLat/2) +
          Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
          Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
      };

      // 6. MAPPER SERVICE → SPÉCIALITÉ
      const getRequiredSpecialty = (serviceType) => {
        const mapping = {
          'Fuite d\'eau': 'urgence',
          'leak': 'urgence',
          'Débouchage': 'débouchage',
          'unclog': 'débouchage',
          'Chauffage': 'chauffage',
          'heating': 'chauffage',
          'Installation': 'installation',
          'installation': 'installation',
          'emergency': 'urgence',
          'Urgence': 'urgence',
        };
        return mapping[serviceType] || 'généraliste';
      };

      // 7. CALCULER LE SCORE
      const scoreTechnician = (tech, appointments, date) => {
        let score = 100;

        if (!isSlotAvailable(tech.id, scheduled_time_start, scheduled_time_end, date)) {
          return -1;
        }

        const requiredSpecialty = getRequiredSpecialty(service_type);
        const techSpecialty = tech.specialty || 'généraliste';

        if (techSpecialty === requiredSpecialty) {
          score += 50;
        } else if (techSpecialty === 'généraliste') {
          score += 20;
        }

        const techAppointments = appointments?.filter(apt => apt.technician === tech.id) || [];
        
        if (techAppointments.length > 0) {
          const lastAppointment = techAppointments
            .filter(apt => apt.scheduled_time_end <= scheduled_time_start)
            .sort((a, b) => b.scheduled_time_end.localeCompare(a.scheduled_time_end))[0];

          if (lastAppointment) {
            const distance = calculateDistance(
              lastAppointment.customer_latitude,
              lastAppointment.customer_longitude,
              customer_latitude,
              customer_longitude
            );

            if (distance < 5) score += 30;
            else if (distance < 10) score += 15;
            else if (distance < 20) score += 5;
            if (distance > 30) score -= 20;
          }
        }

        const techAppointmentsCount = techAppointments.length;
        
        if (techAppointmentsCount >= 8) score -= 40;
        else if (techAppointmentsCount >= 6) score -= 20;
        else if (techAppointmentsCount >= 4) score -= 10;
        else if (techAppointmentsCount === 0) score += 15;

        if (urgency === 'urgent' || urgency === 'Urgent' || urgency === 'emergency') {
          if (techSpecialty === 'urgence') {
            score += 40;
          }
        }

        return score;
      };

      // 8. ÉVALUER ET TRIER
      const technicianScores = allTechnicians.map(tech => ({
        technician: tech,
        score: scoreTechnician(tech, existingAppointments, scheduled_date)
      })).filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score);

      console.log('🎯 Scores des techniciens:', technicianScores.map(t => ({
        name: t.technician.full_name,
        score: t.score
      })));

      if (technicianScores.length > 0) {
        return technicianScores[0].technician.id;
      }

      return null;
    } catch (error) {
      console.error('Erreur assignation technicien:', error);
      return null;
    }
  },

  // Récupérer tous les techniciens depuis user_profiles
  getAllTechnicians: async () => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('role', 'technicien')
      .eq('is_active', true);

    if (error) throw error;
    
    return data.map(tech => ({
      id: tech.id,
      name: tech.full_name,
      email: tech.email,
      phone: tech.phone,
      avatarUrl: tech.avatar_url || tech.avatar,
      specialty: tech.specialty || 'Plombier',
      status: 'available',
      currentLocation: null,
      rating: null,
      reviewCount: null,
      maxDailyServices: 8
    }));
  },

  // Récupérer les absences d'un technicien
  getTechnicianTimeOff: async (technicianId, date) => {
    const dateStr = date.toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('technician_timeoff')
      .select('*')
      .eq('technician_id', technicianId)
      .lte('start_date', dateStr)
      .gte('end_date', dateStr);

    if (error) throw error;
    return data || [];
  },

  // Vérifier si un technicien est disponible à une date/heure donnée
  async isTechnicianAvailable(technicianId, date, time = null) {
    const dateStr = date.toISOString().split('T')[0];
    
    // Vérifier les absences
    const { data: timeoff, error } = await supabase
      .from('technician_timeoff')
      .select('*')
      .eq('technician_id', technicianId)
      .lte('start_date', dateStr)
      .gte('end_date', dateStr);

    if (error) throw error;

    if (timeoff && timeoff.length > 0) {
      for (const to of timeoff) {
        // Absence toute la journée
        if (to.is_all_day) {
          return false;
        }
        
        // Absence à heures spécifiques
        if (time && to.start_time && to.end_time) {
          if (time >= to.start_time && time <= to.end_time) {
            return false;
          }
        }
      }
    }

    return true;
  },

  // Créer une absence
  createTimeOff: async (timeoffData) => {
    const { data, error } = await supabase
      .from('technician_timeoff')
      .insert([timeoffData])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Mettre à jour une absence
  updateTimeOff: async (id, updates) => {
    const { data, error } = await supabase
      .from('technician_timeoff')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Supprimer une absence
  deleteTimeOff: async (id) => {
    const { error } = await supabase
      .from('technician_timeoff')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  // Récupérer toutes les absences pour une période
  getAllTimeOff: async (startDate, endDate) => {
    const { data, error } = await supabase
      .from('technician_timeoff')
      .select(`
        *,
        technician:user_profiles!technician_timeoff_technician_id_fkey (
          id,
          full_name,
          specialty
        )
      `)
      .gte('start_date', startDate)
      .lte('end_date', endDate)
      .order('start_date', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Récupérer les appointments pour une date spécifique
  getAppointmentsByDate: async (date) => {
    const dateStr = date.toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        technician:user_profiles!appointments_technician_fkey (
          id,
          full_name,
          email,
          phone,
          avatar_url
        )
      `)
      .eq('scheduled_date', dateStr)
      .order('scheduled_time_start', { ascending: true });

    if (error) throw error;

    return data.map(apt => ({
      id: apt.id,
      reference: apt.reference,
      technicianId: apt.technician?.id || null,
      technicianName: apt.technician?.full_name || 'Non assigné',
      serviceType: apt.service_type,
      customerName: apt.customer_name,
      scheduledTime: apt.scheduled_time_start,
      scheduledDate: apt.scheduled_date,
      urgency: apt.urgency,
      status: apt.status
    }));
  },

  // Récupérer tous les appointments actifs
  getAllActiveAppointments: async () => {
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        technician:user_profiles!appointments_technician_fkey (
          id,
          full_name,
          email,
          phone,
          avatar_url,
          avatar
        )
      `)
      .order('scheduled_date', { ascending: true })
      .order('scheduled_time_start', { ascending: true });

    if (error) throw error;

    return data.map(apt => ({
      id: apt.id,
      reference: apt.reference,
      technicianId: apt.technician?.id || null,
      technicianName: apt.technician?.full_name || 'Non assigné',
      serviceType: apt.service_type,
      customerName: apt.customer_name,
      customerEmail: apt.customer_email,
      customerPhone: apt.customer_phone,
      customerAddress: apt.customer_address,
      scheduledTime: apt.scheduled_time_start,
      scheduledDate: apt.scheduled_date,
      urgency: apt.urgency,
      status: apt.status,
      notes: apt.notes,
      priceTotal: apt.price_total
    }));
  },

  // Subscribe aux changements de technicians
  subscribeToAllTechnicianLocations(callback) {
    const channel = supabase
      .channel('all-technician-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: 'role=eq.technician'
        },
        (payload) => {
          callback({
            technicianId: payload.new.id,
            status: 'available',
            name: payload.new.full_name,
            email: payload.new.email,
            phone: payload.new.phone,
            timestamp: new Date().toISOString()
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  // Subscribe aux changements d'appointments
  subscribeToAppointmentChanges(callback) {
    const channel = supabase
      .channel('appointment-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments'
        },
        async (payload) => {
          if (payload.new?.technician) {
            const { data: techData } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', payload.new.technician)
              .single();
            
            callback({
              appointment: payload.new,
              technician: techData
            });
          } else {
            callback({
              appointment: payload.new,
              technician: null
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  // Subscribe aux changements d'absences
  subscribeToTimeOffChanges(callback) {
    const channel = supabase
      .channel('timeoff-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'technician_timeoff'
        },
        (payload) => {
          callback(payload);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  // Calculer le statut d'un technicien
  async getTechnicianStatus(technicianId) {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().split(' ')[0].substring(0, 5);
    
    // Vérifier d'abord les absences
    const { data: timeoff } = await supabase
      .from('technician_timeoff')
      .select('*')
      .eq('technician_id', technicianId)
      .lte('start_date', today)
      .gte('end_date', today);

    if (timeoff && timeoff.length > 0) {
      for (const to of timeoff) {
        if (to.is_all_day) {
          return 'timeoff';
        }
        if (to.start_time && to.end_time) {
          if (currentTime >= to.start_time && currentTime <= to.end_time) {
            return 'timeoff';
          }
        }
      }
    }
    
    // Ensuite vérifier les appointments
    const { data, error } = await supabase
      .from('appointments')
      .select('status, scheduled_date, scheduled_time_start, scheduled_time_end')
      .eq('technician', technicianId)
      .eq('scheduled_date', today)
      .in('status', ['planned', 'en_route']);

    if (error) throw error;

    if (data && data.length > 0) {
      return data[0].status === 'planned' ? 'available' : 'busy';
    }
    
    return 'available';
  },

  // Récupérer le nombre d'appointments d'un technicien
  async getTechnicianAppointmentCount(technicianId, date) {
    const dateStr = date.toISOString().split('T')[0];
    
    const { count, error } = await supabase
      .from('appointments')
      .select('*', { count: 'exact', head: true })
      .eq('technician', technicianId)
      .eq('scheduled_date', dateStr);

    if (error) throw error;
    return count || 0;
  },
async getAppointmentDetails (appointmentId) {
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        technician:user_profiles!appointments_technician_fkey (
          id,
          full_name,
          email,
          phone,
          avatar_url
        )
      `)
      .eq('id', appointmentId)
      .single();

    if (error) throw error;

    return {
      id: data.id,
      reference: data.reference,
      technicianId: data.technician?.id || null,
      technicianName: data.technician?.full_name || 'Non assigné',
      serviceType: data.service_type,
      customerName: data.customer_name,
      customerEmail: data.customer_email,
      customerPhone: data.customer_phone,
      customerAddress: data.customer_address,
      scheduledTime: data.scheduled_time_start,
      scheduledDate: data.scheduled_date,
      urgency: data.urgency,
      status: data.status,  
      notes: data.notes,
      priceTotal: data.price_total
    };
  }

};